// HU-47 · Destinatarios de los avisos por correo. F-02, Sprint 1.
//
// La pantalla que evita que "avisar al Comite" sea una lista quemada en el codigo.
// HU-10 depende de ella: sin destinatarios configurados, un caso se registra y no se
// entera nadie — y el sistema no puede distinguir "no habia a quien avisar" de "el
// aviso fallo". Por eso la pantalla insiste tanto cuando la lista esta vacia.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Mail, MailX, Plus, Pencil, Play, Pause, Search, TriangleAlert } from 'lucide-react';
import {
  Alert, Button, Card, Checkbox, DataTable, EmptyState, Field, Input, Modal, Mono,
  PageHeader, Select, StatusBadge,
} from '../components/ui';
import SelectorDePais from '../components/ui/SelectorDePais';
import * as api from '../api/destinatarios';
import { nombreDelTipoDeAviso, opcionesDeTipoDeAviso } from '../lib/tiposDeAviso';
import { useCatalogos } from '../lib/CatalogosContexto';
import { useDiferido } from '../lib/useDiferido';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import type { ColumnaDeTabla, FilaDeTabla } from '../components/ui';
import type { PaisDelFormulario } from '../lib/CatalogosContexto';

const VACIO = { tipoAviso: 'caso_registrado', correo: '', nombre: '', cargo: '', pais: '' };

/**
 * CP-47-02 y CP-47-09 · lo que falta, DICHO EN EL CAMPO (5-sep-2026).
 *
 * La regla siempre estuvo: el servidor rechaza lo incompleto y el correo mal formado. Lo
 * que no habia era pantalla: se mandaba igual y se pintaba el error crudo de la frontera
 * —"String should match pattern ..."— que es lo que QA reporto como no entendible.
 *
 * Los topes y el patron son LOS MISMOS QUE EL SERVIDOR, copiados a proposito de
 * `esquemas.py` del contexto de notificaciones. Que el cliente sea MAS estricto seria
 * peor que no validar: bloquearia altas que el servidor acepta, y el usuario no tendria
 * forma de saber por que. El servidor sigue validando igual; esto solo se adelanta.
 */
const TOPES = { nombre: 200, cargo: 200, correo: 320 };
const PATRON_DE_CORREO = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Un destinatario de aviso, tal como lo devuelve el servidor. */
type Destinatario = {
  destinatario_id: string;
  tipo_aviso: string;
  correo: string;
  nombre: string;
  cargo: string;
  pais?: string | null;
  /** Pausado es `activo: false`. El servidor no borra destinatarios, los desactiva. */
  activo?: boolean;
};

/** El formulario de alta o edicion. Todo texto: es lo que guardan los inputs. */
type FormularioDeDestinatario = {
  /** Presente solo al EDITAR: si viene, se actualiza; si no, se crea. */
  destinatario_id?: string;
  tipoAviso: string;
  correo: string;
  nombre: string;
  cargo: string;
  pais: string;
  /** El ambito con el que se abrio el formulario, para saber si de verdad cambio (CP-47-04). */
  ambitoOriginal?: string;
};

/**
 * ¿Ese correo ya está dado de alta? (pedido del operador, 8-sep-2026).
 *
 * Se compara contra la lista que la pantalla ya tiene cargada, que incluye a los
 * PAUSADOS: un destinatario en pausa sigue ocupando su correo, y darlo de alta otra vez
 * crearía dos filas para la misma persona en vez de reactivar la que existe.
 *
 * Se normaliza —recorte y minúsculas— porque el correo no distingue mayúsculas y
 * `Ana@x.pe` y `ana@x.pe` son la misma bandeja.
 *
 * Al EDITAR se excluye la fila propia: si no, cambiar el cargo de alguien reclamaría su
 * propio correo como repetido y no se podría guardar nada.
 */
const quienTieneElCorreo = (
  correo: string | null | undefined,
  lista: Destinatario[] | null | undefined,
  idPropio: string | undefined,
): Destinatario | undefined => {
  const buscado = String(correo ?? '').trim().toLowerCase();
  if (!buscado) return undefined;
  return (lista ?? []).find(
    (x) => x.destinatario_id !== idPropio
      && String(x.correo ?? '').trim().toLowerCase() === buscado,
  );
};

/**
 * El texto del correo repetido. Si el que lo ocupa esta EN PAUSA se dice, porque la
 * accion correcta no es insistir con el alta sino reactivarlo (21-sep-2026): la lista
 * puede tener al pausado escondido tras «Mostrar en pausa» y el rechazo, sin esta
 * frase, parecia injustificado.
 */
const TEXTO_CORREO_REPETIDO = 'Ese correo ya se encuentra registrado';
const TEXTO_CORREO_EN_PAUSA = `${TEXTO_CORREO_REPETIDO} y está en pausa: reactívalo desde la lista`;

/**
 * ⚠ ESTA REGLA ES MAS ESTRICTA QUE LA DE LA BASE, y es a proposito (operador, 8-sep-2026).
 *
 * La base impide repetir la TERNA (tipo de aviso, pais, correo) —ver
 * `ux_destinatario_sin_duplicados`—, asi que admitiria el mismo correo dos veces con
 * distinto aviso o distinto pais. El operador pidio que la pantalla no lo permita: un
 * correo, un destinatario.
 *
 * CONSECUENCIA ASUMIDA: para que una misma persona reciba DOS tipos de aviso ya no
 * bastara con darla de alta dos veces. Queda anotado porque no esta en la HU.
 */
function faltantesDelDestinatario(
  d: Partial<FormularioDeDestinatario> | null | undefined,
  lista: Destinatario[] | null | undefined = [],
): Record<string, string> {
  const faltan: Record<string, string> = {};
  if (!String(d?.nombre ?? '').trim()) faltan.nombre = 'Escribe el nombre de la persona';
  if (!String(d?.cargo ?? '').trim()) faltan.cargo = 'Escribe el cargo';
  const correo = String(d?.correo ?? '').trim();
  if (!correo) faltan.correo = 'Escribe el correo al que llegará el aviso';
  else if (!PATRON_DE_CORREO.test(correo)) faltan.correo = 'No parece un correo válido. Debe tener la forma nombre@dominio.pe';
  else {
    const ocupante = quienTieneElCorreo(correo, lista, d?.destinatario_id);
    if (ocupante) faltan.correo = ocupante.activo ? TEXTO_CORREO_REPETIDO : TEXTO_CORREO_EN_PAUSA;
  }
  if (!d?.tipoAviso) faltan.tipoAviso = 'Elige qué avisos va a recibir';
  return faltan;
}

/**
 * El correo repetido lo impide la BASE, con el indice `ux_destinatario_sin_duplicados`
 * sobre (tipo de aviso, pais, correo). La misma persona SI puede estar dada de alta para
 * varios paises —es lo normal—; lo que no se admite es la terna exacta repetida.
 *
 * Sin esto, esa colision llegaba a la pantalla como el error tecnico de la restriccion.
 */
const esCorreoRepetido = (error: unknown): boolean => {
  const e = error as { message?: string; cuerpo?: { detalle?: string } } | null;
  return /duplicad|unique|ux_destinatario/i.test(`${e?.message ?? ''} ${e?.cuerpo?.detalle ?? ''}`);
};

/**
 * El contador de caracteres, EXACTAMENTE como en el formulario de registro.
 *
 * ⚠ Estaba mal (06-sep-2026, reportado por el operador). Decia `${tope - usados}
 * caracteres`, o sea los que QUEDAN, pero sin decirlo: para un nombre de 6 letras ponia
 * "194 caracteres", que se lee como "este campo tiene 194 caracteres". Y al teclear el
 * numero BAJABA —194, 193, 192—, lo que parece que el campo esta perdiendo algo.
 *
 * La forma correcta es "usados / tope", que es la que usa el resto de la aplicacion. Que
 * dos pantallas cuenten distinto lo mismo es peor que no contar.
 */
const cuentaAtras = (valor: unknown, tope?: number): string | undefined =>
  (tope ? `${String(valor ?? '').length} / ${tope} caracteres` : undefined);

// La etiqueta sale del catálogo único (`lib/tiposDeAviso.ts`): la misma que ofrece el
// desplegable. Un código que no esté allí se muestra tal cual (la lista es abierta).
const nombreDeTipo = (codigo: string): string => nombreDelTipoDeAviso(codigo);

// Recibe la lista en vez de cerrarse sobre una constante: los paises llegan del API
// (`/referencias/paises`) y ya no hay un modulo del front del que leerlos. Si el codigo
// no esta en la lista se muestra el codigo, que es informacion y no un hueco: un
// destinatario puede estar asignado a un pais que el catalogo ya desactivo.
const nombreDePais = (paises: PaisDelFormulario[], cod?: string | null): string | null =>
  (cod ? paises.find((p) => p.cod === cod)?.nombre ?? cod : null);

/** El formulario en blanco. Sembrarlo evita que el objeto exista a medias. */
const FORMULARIO_VACIO: FormularioDeDestinatario = {
  tipoAviso: '', correo: '', nombre: '', cargo: '', pais: '',
};

/** Valor del filtro de ámbito que significa "solo los que reciben de los 14 países". */
const AMBITO_GLOBAL = '__todos__';

/** Sin tildes ni mayúsculas, para que «Díaz» se encuentre escribiendo «diaz». */
const normalizar = (s: string | null | undefined): string =>
  (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * EL MISMO ORDEN QUE LA API (`consulta_de_listado` del backend): por tipo de aviso,
 * dentro de cada aviso los que RECIBEN antes que los pausados, y por correo. Se repite
 * aquí para que la pantalla no dependa de que el servidor lo respete —y para que al
 * pausar a alguien su fila baje al final de SU grupo, no al final de la tabla—.
 */
export const ordenarDestinatarios = (lista: Destinatario[]): Destinatario[] =>
  [...lista].sort((a, b) =>
    a.tipo_aviso.localeCompare(b.tipo_aviso)
    || Number(Boolean(b.activo)) - Number(Boolean(a.activo))
    || normalizar(a.correo).localeCompare(normalizar(b.correo)));

type FiltrosDeDestinatarios = {
  texto: string;
  tipoAviso: string;
  ambito: string;
  mostrarPausados: boolean;
  nombreDePais: (cod: string) => string | null;
};

/** Filtra en memoria: texto libre (nombre, cargo, correo, aviso, país), aviso, ámbito y estado. */
export function filtrarDestinatarios(lista: Destinatario[], f: FiltrosDeDestinatarios): Destinatario[] {
  const texto = normalizar(f.texto);
  const filtrados = lista.filter((d) => {
    if (!f.mostrarPausados && !d.activo) return false;
    if (f.tipoAviso && d.tipo_aviso !== f.tipoAviso) return false;
    if (f.ambito === AMBITO_GLOBAL && d.pais) return false;
    if (f.ambito && f.ambito !== AMBITO_GLOBAL && d.pais !== f.ambito) return false;
    if (!texto) return true;
    const pajar = normalizar([
      d.nombre, d.cargo, d.correo, nombreDeTipo(d.tipo_aviso), d.pais ? f.nombreDePais(d.pais) : 'todos los paises',
    ].join(' '));
    return pajar.includes(texto);
  });
  return ordenarDestinatarios(filtrados);
}

type GrupoDeAviso = { tipoAviso: string; filas: Destinatario[]; activos: number; pausados: number };

/** Un bloque por tipo de aviso, en el orden en que llegan (ya ordenados). */
export function agruparPorAviso(lista: Destinatario[]): GrupoDeAviso[] {
  const grupos: GrupoDeAviso[] = [];
  for (const d of lista) {
    let grupo = grupos.at(-1);
    if (grupo?.tipoAviso !== d.tipo_aviso) {
      grupo = { tipoAviso: d.tipo_aviso, filas: [], activos: 0, pausados: 0 };
      grupos.push(grupo);
    }
    grupo.filas.push(d);
    if (d.activo) grupo.activos += 1; else grupo.pausados += 1;
  }
  return grupos;
}

export default function Destinatarios() {
  const { PAISES } = useCatalogos();
  const [lista, setLista] = useState<Destinatario[] | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [editando, setEditando] = useState<FormularioDeDestinatario | null>(null); // null | {…} para alta o edicion
  const [guardando, setGuardando] = useState(false);
  const [errorDelFormulario, setErrorDelFormulario] = useState<Fallo | null>(null);
  // Lo que falta, por campo. Vacio hasta que se intenta guardar: senalar en rojo un
  // formulario recien abierto es acusar de un error que nadie ha cometido todavia.
  const [faltantes, setFaltantes] = useState<Record<string, string>>({});

  const recargar = useCallback(async () => {
    setCargando(true);
    setFallo(null);
    try {
      setLista((await api.listarDestinatarios()) as Destinatario[]);
    } catch (error) {
      setFallo(aFallo(error));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    recargar();
  }, [recargar]);

  // HU-10 no puede cumplirse sin al menos un destinatario ACTIVO de `caso_registrado`.
  // Se comprueba aqui y se avisa arriba: descubrirlo cuando un caso real no genera
  // avisos es tarde, y el sintoma —"no me llego nada"— no apunta a esta pantalla.
  const sinQuienReciba = useMemo(() => {
    if (!lista) return false;
    return !lista.some((d) => d.tipo_aviso === 'caso_registrado' && d.activo);
  }, [lista]);

  // Filtros de la lista (21-sep-2026). Actúan sobre lo YA CARGADO —la lista llega
  // entera, es pequeña por naturaleza— así que no hay viaje al servidor por tecla ni por
  // filtro. El buscador se asienta a los 300 ms como en las bandejas.
  const [busqueda, setBusqueda] = useState('');
  const busquedaDiferida = useDiferido(busqueda);
  const [filtroAviso, setFiltroAviso] = useState('');
  const [filtroAmbito, setFiltroAmbito] = useState('');
  // Por defecto SE VEN: ocultar a los pausados de entrada haría que "no encuentro a
  // Luis" fuese la primera experiencia de quien viene a reactivarlo.
  const [mostrarPausados, setMostrarPausados] = useState(true);

  const visibles = useMemo(
    () => filtrarDestinatarios(lista ?? [], {
      texto: busquedaDiferida,
      tipoAviso: filtroAviso,
      ambito: filtroAmbito,
      mostrarPausados,
      nombreDePais: (cod) => nombreDePais(PAISES, cod),
    }),
    [lista, busquedaDiferida, filtroAviso, filtroAmbito, mostrarPausados, PAISES],
  );
  const grupos = useMemo(() => agruparPorAviso(visibles), [visibles]);
  const hayFiltro = Boolean(busqueda || filtroAviso || filtroAmbito || !mostrarPausados);

  const opcionesDeAviso = useMemo(() => {
    const conteo = new Map<string, number>();
    for (const d of lista ?? []) conteo.set(d.tipo_aviso, (conteo.get(d.tipo_aviso) ?? 0) + 1);
    return [
      { value: '', label: 'Todos los avisos' },
      ...[...conteo.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([codigo, n]) => ({ value: codigo, label: `${nombreDeTipo(codigo)} (${n})` })),
    ];
  }, [lista]);
  const opcionesDeAmbito = useMemo(() => {
    const codigos = [...new Set((lista ?? []).map((d) => d.pais).filter((p): p is string => Boolean(p)))].sort((a, b) => a.localeCompare(b));
    return [
      { value: '', label: 'Cualquier ámbito' },
      { value: AMBITO_GLOBAL, label: 'Solo «Todos los países»' },
      ...codigos.map((cod) => ({ value: cod, label: `Solo ${nombreDePais(PAISES, cod)}` })),
    ];
  }, [lista, PAISES]);

  const limpiarFiltros = () => {
    setBusqueda(''); setFiltroAviso(''); setFiltroAmbito(''); setMostrarPausados(true);
  };

  const alternarPausa = async (destinatario: Destinatario) => {
    setFallo(null);
    try {
      if (destinatario.activo) await api.pausarDestinatario(destinatario.destinatario_id);
      else await api.reactivarDestinatario(destinatario.destinatario_id);
      await recargar();
    } catch (error) {
      setFallo(aFallo(error));
    }
  };

  const guardar = async () => {
    // CP-47-02: no se manda nada hasta que lo obligatorio este. Antes se enviaba igual y
    // el usuario descubria que faltaba un campo leyendo el error de la frontera.
    const loQueFalta = faltantesDelDestinatario(editando, lista ?? []);
    setFaltantes(loQueFalta);
    if (Object.keys(loQueFalta).length) {
      setErrorDelFormulario(null);
      return;
    }

    setGuardando(true);
    setErrorDelFormulario(null);
    try {
      if (editando?.destinatario_id) {
        // CP-47-04: la bandera solo cuando el ambito cambio DE VERDAD. `ambitoOriginal`
        // se guarda al abrir el formulario, para poder comparar.
        await api.actualizarDestinatario(editando.destinatario_id, editando, {
          cambiarAmbito: (editando.pais ?? '') !== (editando.ambitoOriginal ?? ''),
        });
      } else {
        await api.crearDestinatario(editando as FormularioDeDestinatario);
      }
      setEditando(null);
      setFaltantes({});
      await recargar();
    } catch (error) {
      // El correo mal escrito lo rechaza el dominio, no el navegador: HU-47 dice que
      // "un aviso que no llega es indistinguible de un aviso que nadie mando".
      //
      // La colision de la terna (tipo de aviso, pais, correo) se traduce, porque el texto
      // de la restriccion no le dice nada a quien esta rellenando el formulario.
      if (esCorreoRepetido(error)) {
        setFaltantes((p) => ({
          ...p,
          // El MISMO texto que la comprobacion del front: que el servidor y la pantalla
          // digan lo mismo del mismo hecho. Este camino queda como red de seguridad —la
          // lista cargada podria estar desfasada si alguien dio de alta desde otra sesion—.
          correo: TEXTO_CORREO_REPETIDO,
        }));
        setErrorDelFormulario(null);
      } else {
        setErrorDelFormulario(aFallo(error));
      }
    } finally {
      setGuardando(false);
    }
  };

  // La columna «Qué avisos recibe» ya no va por fila: cada bloque de la tabla ES un tipo
  // de aviso y lo dice en su cabecera. La de acciones lleva rótulo solo para lectores de
  // pantalla, como en las bandejas (cabecera alineada con lo que hay debajo).
  // Anchos fijos: hay una tabla por bloque y las columnas deben caer en el mismo sitio
  // en todos los bloques; sin esto cada tabla las repartía según su contenido.
  const columnas: ColumnaDeTabla[] = [
    { key: 'nombre', label: 'Destinatario', className: 'w-[44%]' },
    { key: 'ambito', label: 'Ámbito', className: 'w-[18%]' },
    { key: 'activo', label: 'Estado', className: 'w-[18%]' },
    { key: 'acciones', label: <span className="sr-only">Acciones</span>, className: 'w-[20%]' },
  ];

  // `DataTable` pasa el OBJETO columna, no su clave. Recibirlo como cadena hacia
  // que ninguna rama coincidiera y cada celda cayera al `return` final: cinco
  // botones de accion por fila y ningun dato.
  const celda = (filaGenerica: FilaDeTabla, { key: columna }: ColumnaDeTabla) => {
    const fila = filaGenerica as unknown as Destinatario;
    // Un pausado se ve ATENUADO, no solo con su badge: la tabla no admite clases por
    // fila, así que se atenúa cada celda de datos (las acciones no, siguen siendo botones).
    // HU-51 (WCAG 1.4.3): atenuar con `opacity-60` bajaba el texto a 2,64:1. Ahora el
    // texto pasa al gris secundario (6,42:1) y a cursiva: se nota apagado y se lee.
    const atenuado = (contenido: ReactNode) =>
      (fila.activo ? contenido : <div className="italic text-ink-soft [&_*]:text-ink-soft" data-pausado="true">{contenido}</div>);
    if (columna === 'nombre') {
      return atenuado(
        <div>
          <p className="font-semibold text-ink text-body">{fila.nombre}</p>
          <p className="text-meta text-ink-soft">{fila.cargo}</p>
          <Mono className="text-meta text-ink-soft">{fila.correo}</Mono>
        </div>,
      );
    }
    if (columna === 'ambito') {
      // `pais: null` es "todos los paises", no un dato faltante (RN-02, RNF-08).
      return atenuado(
        fila.pais ? nombreDePais(PAISES, fila.pais) : <em className="text-ink-soft">Todos los países</em>,
      );
    }
    if (columna === 'activo') {
      return fila.activo ? (
        <StatusBadge tone="success" icon={Mail}>Recibe avisos</StatusBadge>
      ) : (
        <StatusBadge tone="neutral" icon={MailX}>En pausa</StatusBadge>
      );
    }
    return (
      <div className="flex gap-2 justify-end">
        <Button
          size="sm"
          variant="secondary"
          icon={Pencil}
          onClick={() =>
            setEditando({
              destinatario_id: fila.destinatario_id,
              tipoAviso: fila.tipo_aviso,
              correo: fila.correo,
              nombre: fila.nombre,
              cargo: fila.cargo,
              pais: fila.pais ?? '',
              // Copia de referencia para saber si el ambito cambio (CP-47-04). No viaja
              // al servidor: solo decide si se manda `cambiar_ambito`.
              ambitoOriginal: fila.pais ?? '',
            })
          }
        >
          Editar
        </Button>
        <Button
          size="sm"
          variant="secondary"
          icon={fila.activo ? Pause : Play}
          onClick={() => alternarPausa(fila)}
        >
          {fila.activo ? 'Pausar' : 'Reactivar'}
        </Button>
      </div>
    );
  };

  const total = lista?.length ?? 0;
  const resumenDelGrupo = (g: GrupoDeAviso): string => {
    const partes = [`${g.activos} ${g.activos === 1 ? 'recibe' : 'reciben'}`];
    if (g.pausados) partes.push(`${g.pausados} en pausa`);
    return partes.join(' · ');
  };

  return (
    <>
      <PageHeader
        eyebrow="Administración · Destinatarios de avisos"
        hu="HU-47 · F-02"
        title="Destinatarios de los avisos"
        subtitle="Quién recibe cada aviso por correo y de qué países."
        icon={Mail}
        actions={
          <Button icon={Plus} onClick={() => { setFaltantes({}); setEditando({ ...VACIO }); }}>
            Agregar destinatario
          </Button>
        }
      />

      {/* Sin conexion (estado 0) se dice que hacer, como en las bandejas (lineamiento,
          «Estados · Sin conexión»). HU-51, tarea 51-9. */}
      {fallo && (
        <Alert tone="danger" title="No se pudo completar la operación" detalleTecnico={fallo.detalleTecnico}>
          {fallo.message}
          {fallo.estado === 0 && ' Revisa tu conexión e intenta refrescar.'}
        </Alert>
      )}

      {sinQuienReciba && !cargando && (
        <Alert tone="warning" title="Nadie recibiría el aviso de un caso nuevo" icon={TriangleAlert}>
          No hay ningún destinatario activo para el aviso «Caso registrado». Mientras esta lista esté vacía,
          los casos se registrarán <strong>y no se enterará nadie</strong>.
        </Alert>
      )}

      {/* Si la carga fallo no queda nada que mostrar en la tarjeta: ni lista ni vacio. Una
          tarjeta en blanco bajo el aviso pareceria una lista vacia (51-9). */}
      {(cargando || lista !== null) && (
      <Card className="mt-4" pad={false}>
        {/* Tres estados excluyentes —cargando, con datos, vacio— escritos como tres
            condiciones y no como ternarios encadenados: asi cada uno se lee solo. */}
        {cargando && (
          <p className="p-6 text-body text-ink-soft">Cargando destinatarios…</p>
        )}
        {!cargando && total > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-hairline">
              <div className="relative flex-1 basis-full sm:basis-auto sm:min-w-[13.75rem]">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
                <Input
                  aria-label="Buscar destinatario"
                  placeholder="Buscar por nombre, cargo o correo…"
                  className="pl-9"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>
              <div className="w-full sm:w-60">
                <Select
                  aria-label="Filtrar por aviso"
                  sinVacio
                  value={filtroAviso}
                  onChange={(e) => setFiltroAviso(String(e.target.value ?? ''))}
                  options={opcionesDeAviso}
                />
              </div>
              <div className="w-full sm:w-60">
                <Select
                  aria-label="Filtrar por ámbito"
                  sinVacio
                  value={filtroAmbito}
                  onChange={(e) => setFiltroAmbito(String(e.target.value ?? ''))}
                  options={opcionesDeAmbito}
                />
              </div>
              <Checkbox
                checked={mostrarPausados}
                onChange={(e) => setMostrarPausados(e.target.checked)}
                label="Mostrar en pausa"
              />
            </div>
            <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-hairline">
              <p className="text-meta text-ink-soft" aria-live="polite">
                {`Mostrando ${visibles.length} de ${total} ${total === 1 ? 'destinatario' : 'destinatarios'}`}
              </p>
              {hayFiltro && (
                <Button size="sm" variant="secondary" onClick={limpiarFiltros}>Quitar filtros</Button>
              )}
            </div>
            {grupos.map((g) => (
              <section key={g.tipoAviso} aria-label={nombreDeTipo(g.tipoAviso)}>
                {/* El nombre del aviso va en su propio <span>: es la etiqueta que se busca
                    en pantalla; el resumen a su lado no la contamina. */}
                <h3 className="flex items-baseline gap-2 px-4 py-2 bg-canvas border-t border-hairline">
                  <span className="text-body font-semibold text-ink">{nombreDeTipo(g.tipoAviso)}</span>
                  <span className="text-meta text-ink-soft">{`· ${resumenDelGrupo(g)}`}</span>
                </h3>
                <DataTable
                  columns={columnas}
                  rows={g.filas.map((d) => ({ ...d, id: d.destinatario_id })) as unknown as FilaDeTabla[]}
                  renderCell={celda}
                  sinMarco
                />
              </section>
            ))}
            {!visibles.length && (
              <EmptyState
                icon={Search}
                title="Ningún destinatario coincide con el filtro"
                description="Prueba con otro texto, otro aviso u otro ámbito, o muestra también a los que están en pausa."
                action={<Button variant="secondary" onClick={limpiarFiltros}>Quitar filtros</Button>}
              />
            )}
          </>
        )}
        {/* `lista` y no `total`: si la carga FALLO no hay lista, y «Todavía no hay
            destinatarios» afirmaba algo que la pantalla no sabe (§8.1, la pantalla dice la
            verdad). Con el fallo se ve solo el aviso de error de arriba (HU-51, tarea 51-9). */}
        {!cargando && lista !== null && !total && (
          <EmptyState
            icon={MailX}
            title="Todavía no hay destinatarios"
            description="Agrega al menos uno para que alguien reciba el aviso de cada caso registrado."
            action={
              <Button icon={Plus} onClick={() => { setFaltantes({}); setEditando({ ...VACIO }); }}>
                Agregar el primero
              </Button>
            }
          />
        )}
      </Card>
      )}

      <Modal
        open={Boolean(editando)}
        onClose={() => { setEditando(null); setFaltantes({}); setErrorDelFormulario(null); }}
        eyebrow="Administración · Destinatarios de avisos"
        title={editando?.destinatario_id ? 'Editar destinatario' : 'Nuevo destinatario'}
        icon={Mail}
        footer={
          <>
            <Button variant="secondary"
              onClick={() => { setEditando(null); setFaltantes({}); setErrorDelFormulario(null); }}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </Button>
          </>
        }
      >
        {errorDelFormulario && (
          <Alert tone="danger" title="No se pudo guardar">
            {errorDelFormulario.message}
          </Alert>
        )}
        <div className="space-y-4 mt-2">
          <Field label="Nombre" required error={faltantes.nombre}
            contador={cuentaAtras(editando?.nombre, TOPES.nombre)}>
            <Input
              value={editando?.nombre ?? ''}
              maxLength={TOPES.nombre}
              onChange={(e) => setEditando((p) => ({ ...(p ?? FORMULARIO_VACIO), nombre: e.target.value }))}
            />
          </Field>
          <Field label="Cargo" required hint="Aparece en el aviso, para que se sepa por qué lo recibe"
            error={faltantes.cargo} contador={cuentaAtras(editando?.cargo, TOPES.cargo)}>
            <Input
              value={editando?.cargo ?? ''}
              maxLength={TOPES.cargo}
              onChange={(e) => setEditando((p) => ({ ...(p ?? FORMULARIO_VACIO), cargo: e.target.value }))}
            />
          </Field>
          <Field label="Correo" required hint="Revisa que esté bien escrito: a este correo llega el aviso"
            error={faltantes.correo} contador={cuentaAtras(editando?.correo, TOPES.correo)}>
            <Input
              type="email"
              value={editando?.correo ?? ''}
              maxLength={TOPES.correo}
              onChange={(e) => setEditando((p) => ({ ...(p ?? FORMULARIO_VACIO), correo: e.target.value }))}
              /* Al SALIR del campo, no al teclear: avisar mientras se escribe marcaria en
                 rojo cada correo a medio escribir. Aqui ya se termino de escribir. */
              onBlur={() => setFaltantes((p) => ({
                ...p,
                correo: faltantesDelDestinatario(editando, lista ?? []).correo,
              }))}
            />
          </Field>
          {/* EDITABLE desde el 06-sep-2026 (decisión del operador). Estuvo bloqueado al
              editar —solo se elegía al dar de alta— con el argumento de que cambiarlo
              rompería la trazabilidad de los avisos ya enviados. Ese argumento NO se
              sostiene: `aviso_enviado` guarda COPIAS del nombre, el cargo y el correo, no
              una referencia al destinatario, así que el histórico no depende de esta fila.
              Lo que quedaba era una incoherencia de uso: al crear se elegía y al corregir
              no, sin regla de negocio que lo justificara. HU-47 tampoco lo prohíbe.
              Lo que sí sigue protegido es el índice único (tipo de aviso, país, correo):
              cambiarlo hacia una terna que ya existe choca, y se explica en el campo. */}
          <Field label="Qué avisos recibe" required error={faltantes.tipoAviso}
            hint="Se puede corregir. Si ya existe otro destinatario con el mismo correo, aviso y ámbito, el sistema lo avisa.">
            <Select
              value={editando?.tipoAviso ?? ''}
              onChange={(e) => setEditando((p) => ({ ...(p ?? FORMULARIO_VACIO), tipoAviso: String(e.target.value ?? '') }))}
              options={opcionesDeTipoDeAviso()}
            />
          </Field>
          <Field
            label="Ámbito"
            hint="Déjalo en «Todos los países» si debe recibir avisos de los 14"
          >
            {/* Bandera y nombre del país, con la autoridad como segunda línea (antes iba
                pegada al nombre con «·»). La opción la arma `SelectorDePais` (operador, 5-oct-2026). */}
            <SelectorDePais
              value={editando?.pais ?? ''}
              onChange={(e) => setEditando((p) => ({ ...(p ?? FORMULARIO_VACIO), pais: String(e.target.value ?? '') }))}
              paises={PAISES}
              conAutoridad
              antes={[{ value: '', label: 'Todos los países' }]}
            />
          </Field>
        </div>
      </Modal>
    </>
  );
}
